import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits, getHasLuxPlusInAnyRegion, getIsLuxPlusEnabled, getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { checkoutCartHasLoadedMembershipRestrictedOffer, checkoutCartHasUnresolvedMemberCapableOffer, getLuxPlusMemberOnlyCheckoutBlock, type LuxPlusMemberOnlyCheckoutBlock } from 'luxPlus/selectors/cartEligibility'
import { getAllOffers } from 'selectors/offerSelectors'
import {
  API_CALL, API_CALL_SUCCESS, CLEAR_LUX_PLUS_MEMBER_SUBSCRIPTION,
  SET_SHOW_LUX_PLUS_FREE_PREVIEW_MODAL,
  TOGGLE_SHOP_AS_MEMBER,
} from './actionConstants'
import {
  FETCH_LUX_PLUS_PRODUCT_SUBSCRIPTION,
  FETCH_LUX_PLUS_MEMBER_SUBSCRIPTION,
  REFRESH_LUX_PLUS_MEMBERSHIP_FOR_CART_CLEANUP,
  UPDATE_LUX_PLUS_MEMBER_SUBSCRIPTION,
  UPDATE_LUX_PLUS_MEMBER_PAY_RENEWAL_WITH_POINTS,
  CREATE_LUX_PLUS_MEMBER_FREE_PREVIEW,
  FETCH_LUX_PLUS_MEMBER_FREE_PREVIEW_ELIGIBILITY,
  FETCH_LUX_PLUS_PREFERRED_PAYMENT_METHOD,
  FETCH_LUX_PLUS_RENEWAL_PAYMENT_CARDS,
  FETCH_LUX_PLUS_ORDER_REDEEMED_SAVINGS,
  FETCH_LUX_PLUS_ORDER_POTENTIAL_SAVINGS,
  FETCH_LUX_PLUS_MEMBERSHIP_METRICS,
  FETCH_LUX_PLUS_TOTAL_POTENTIAL_SAVINGS,
  FETCH_LUX_PLUS_ACCUMULATIVE_SAVINGS,
} from './apiActionConstants'
import type { MemberSubscriptionResult, MemberSubscriptionUpdateData } from 'api/luxPlus'
import type { ApiActionResult } from 'middlewares/apiMiddleware'
import {
  getMemberSubscription,
  getMemberSubscriptionEligibility,
  getSubscriptionOffer,
  getMemberSubscriptionPoll,
  updateMemberSubscription as updateMemberSubscriptionRequest,
  createMemberFreePreviewPeriod,
  getMemberFreePreviewEligibility,
  getRedeemedBenefitsByOrderId,
  getMembershipMetrics,
  getTotalPotentialSavings,
  getPotentialSavings,
} from 'api/luxPlus'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getOffSessionPaymentMethodsByRegionAndPurposeId } from 'api/payment'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { ThunkAction } from 'redux-thunk'
import type { AppAction, DefinedAppAction } from './types/ActionTypes'
import { getCurrentIntl } from 'i18n/util/currentIntl'

/**
 * Fetch all subscription offers including soft-deleted (expired) ones.
 * This allows renewal members to find legacy price offers while new members
 * can filter for current active offers by price.
 */
export function fetchProductSubscription(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (
      !getIsLuxPlusEnabled(state) ||
      state.luxPlus.products.subscription.offers ||
      state.luxPlus.products.subscription.fetching ||
      state.luxPlus.products.subscription.error
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_PRODUCT_SUBSCRIPTION,
      request: () => getSubscriptionOffer(state.geo.currentRegionCode, true),
    })
  }
}

interface FetchMemberSubscriptionParams {
  refetch?: boolean
  poll?: boolean
  validate?: (result: App.MemberSubscriptionItem) => boolean
  onComplete?: (result: ApiActionResult<MemberSubscriptionResult | undefined>) => void
}

const checkValidMemberSubscriptionDispatch = (state: App.State, memberId?: string): boolean => {
  if (!memberId || state.auth.account.luxPlus.member.subscription.fetching) {
    return false
  }

  return true
}

export function fetchMemberSubscription(params: FetchMemberSubscriptionParams = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const accessToken = state.auth.accessToken

    const isValidDispatch = checkValidMemberSubscriptionDispatch(state, memberId)
    if (!isValidDispatch || !memberId) return false

    /*
      We want to refetch when a new membership has been bought. We want to prevent fetching
      if service returns an error or if the user account does not have a membership.
     */
    const shouldFetch = !state.auth.account.luxPlus.member.subscription.error && state.auth.account.subscriberTier && !state.auth.account.luxPlus.member.subscription.item && getIsLuxPlusEnabled(state)
    if (params.refetch || shouldFetch) {
      dispatch({
        type: API_CALL,
        api: FETCH_LUX_PLUS_MEMBER_SUBSCRIPTION,
        onComplete: params.onComplete,
        request: () => {
          if (params.poll) {
            return getMemberSubscriptionPoll(memberId, state.geo.currentRegionCode, accessToken, params?.validate)
          } else {
            return getMemberSubscription({
              customerId: memberId,
              regionCode: state.geo.currentRegionCode,
              accessToken,
            })
          }
        },
      })
      return true
    }
    return false
  }
}

export function isReadyPaidMembership(subscription: App.MemberSubscriptionItem): boolean {
  return subscription.tier !== 'free_preview' &&
    (subscription.status === 'ACTIVE' || subscription.status === 'AWAITING_RENEWAL')
}

/**
 * Awaitable strict membership refresh. Resolves with the checkout block computed from the store after the request settles.
 * Does not change the legacy fetch, which still maps every error to "no subscription".
 * `checkoutGate` limits the checkout-cart short-circuit to payment. Cart cleanup omits it and still refreshes.
 */
export function refreshLuxPlusMembershipForCartCleanup(options?: { force?: boolean, checkoutGate?: boolean }): ThunkAction<Promise<LuxPlusMemberOnlyCheckoutBlock>, App.State, undefined, DefinedAppAction> {
  return (dispatch, getState): Promise<LuxPlusMemberOnlyCheckoutBlock> => {
    const readBlock = (): LuxPlusMemberOnlyCheckoutBlock => getLuxPlusMemberOnlyCheckoutBlock(getState(), getAllOffers(getState()))
    const state = getState()
    if (!getIsLuxPlusEnabled(state)) {
      return Promise.resolve({ blocked: false })
    }

    if (options?.checkoutGate) {
      const offers = getAllOffers(state)
      const needsMembershipCheck = checkoutCartHasLoadedMembershipRestrictedOffer(state, offers) || checkoutCartHasUnresolvedMemberCapableOffer(state, offers)
      if (!needsMembershipCheck) {
        return Promise.resolve({ blocked: false })
      }
    }

    const memberId = state.auth.account.memberId
    if (!memberId || state.auth.devOverrideActive) {
      return Promise.resolve(readBlock())
    }

    if (!options?.force && state.auth.account.luxPlus.member.cartEligibilityRefresh?.status === 'fetching') {
      return Promise.resolve(readBlock())
    }

    const regionCode = state.geo.currentRegionCode
    const requestId = uuidV4()
    const requestPromise = getMemberSubscriptionEligibility({
      customerId: memberId,
      regionCode,
      accessToken: state.auth.accessToken,
    })

    dispatch({
      type: API_CALL,
      api: REFRESH_LUX_PLUS_MEMBERSHIP_FOR_CART_CLEANUP,
      memberId,
      regionCode,
      requestId,
      request: () => requestPromise,
    })

    return requestPromise.then(readBlock, readBlock)
  }
}

export function updateMemberSubscription(data: MemberSubscriptionUpdateData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId

    // Points saves must be awaitable without hiding the current subscription on request or failure.
    if ('payRenewalWithPoints' in data) {
      if (!memberId) {
        return Promise.reject(new Error('Cannot update LuxPlus renewal points preference without a member'))
      }

      return updateMemberSubscriptionRequest(memberId, data).then(subscription => {
        dispatch({
          type: API_CALL_SUCCESS,
          api: UPDATE_LUX_PLUS_MEMBER_PAY_RENEWAL_WITH_POINTS,
          data: subscription,
        })
      })
    }

    const isValidDispatch = checkValidMemberSubscriptionDispatch(state, memberId)
    if (!isValidDispatch || !memberId) return

    dispatch({
      type: API_CALL,
      api: UPDATE_LUX_PLUS_MEMBER_SUBSCRIPTION,
      request: () => updateMemberSubscriptionRequest(memberId, data),
    })
  }
}

export function clearMemberSubscription(): AppAction {
  return {
    type: CLEAR_LUX_PLUS_MEMBER_SUBSCRIPTION,
  }
}

export function fetchMemberFreePreviewEligibility(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const freePreview = state.luxPlus.products.freePreview

    if (
      !memberId ||
      !getIsLuxPlusEnabled(state) ||
      freePreview.fetching ||
      freePreview.error ||
      freePreview.offer
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_MEMBER_FREE_PREVIEW_ELIGIBILITY,
      request: () => getMemberFreePreviewEligibility(memberId),
    })
  }
}

export function createFreePreviewMember(options?: {
  /** Distinguishes an automatic signup enrolment from a user-initiated one. */
  trigger?: App.MemberSubscriptionItem['trigger']
  /** Suppress the failure snackbar — used for silent auto-enrolment. */
  silentError?: boolean
}): AppAction {
  const trigger = options?.trigger ?? 'manual-free-preview-enroll'
  const silentError = options?.silentError ?? false

  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const accessToken = state.auth.accessToken
    const subscription = state.auth.account.luxPlus.member.subscription
    const headlessMode = state.system.headlessMode
    let joined = false

    if (
      !memberId ||
      !getIsLuxPlusEnabled(state) ||
      subscription.fetching ||
      subscription.error ||
      getCanViewLuxPlusBenefits(state) ||
      getHasLuxPlusInAnyRegion(state)
    ) return

    dispatch({
      type: API_CALL,
      api: CREATE_LUX_PLUS_MEMBER_FREE_PREVIEW,
      request: () => createMemberFreePreviewPeriod({
        customerId: memberId,
        region: state.geo.currentRegionCode,
      }, accessToken)
        .then((result): App.MemberSubscriptionItem => {
          joined = true
          // Trigger the "you've unlocked a free preview" modal (replaces the old success snackbar).
          // The trigger travels with it because the modal words itself differently depending on
          // whether the customer just registered.
          dispatch({ type: SET_SHOW_LUX_PLUS_FREE_PREVIEW_MODAL, show: true, trigger })
          return {
            ...result,
            trigger,
          }
        })
        .catch((err) => {
          if (!silentError) {
            const intl = getCurrentIntl()
            showSnackbar(
              intl.formatMessage({
                id: 'oSH3002ic-ismpb4jtWj',
                defaultMessage: 'Please try again later or contact us for assistance.',
                description: 'Snackbar body message when LuxPlus free preview enrolment fails',
              }),
              'critical',
              {
                heading: intl.formatMessage({
                  id: '8u2yr88KDeY0JieVo-Mq',
                  defaultMessage: 'Oops! Something went wrong',
                  description: 'Snackbar heading when LuxPlus free preview enrolment fails',
                }),
              },
            )
          }

          return err
        })
        .finally(() => {
          if (headlessMode) {
            appViewMessageHandler('luxPlusFreePreview', { joined })
          }
        }),
    })
  }
}

export function fetchLuxPlusPreferredPaymentCard(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode
    const luxPlusMemberSubscriptionId = state.auth.account.luxPlus.member.subscription.item?.id
    const canRedeemLuxPlusBenefits = getCanRedeemLuxPlusBenefits(state)
    const pendingRenewalSubscribedLuxPlusMember = getIsPendingRenewalLuxPlusMember(state)

    if (!luxPlusMemberSubscriptionId || (!canRedeemLuxPlusBenefits && !pendingRenewalSubscribedLuxPlusMember)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_PREFERRED_PAYMENT_METHOD,
      request: () => getOffSessionPaymentMethodsByRegionAndPurposeId(region, luxPlusMemberSubscriptionId)
        .then(({ cards, paymentMethod }) => {
          const preferredPaymentMethod = cards.find(card => card.paymentMethod === paymentMethod)
          if (!preferredPaymentMethod) {
            throw new Error(`No LuxPlus preferred payment method found for payment method ${paymentMethod}`)
          }

          return {
            ...preferredPaymentMethod,
            card: preferredPaymentMethod.card ? {
              ...preferredPaymentMethod.card,
              expMonth: preferredPaymentMethod.card.expMonth ?? preferredPaymentMethod.expMonth,
              expYear: preferredPaymentMethod.card.expYear ?? preferredPaymentMethod.expYear,
            } : undefined,
          }
        }),
    })
  }
}

interface FetchLuxPlusSubscriptionRenewalPaymentCardsParams {
  refetch?: boolean
}

export function fetchLuxPlusSubscriptionRenewalPaymentCards(params: FetchLuxPlusSubscriptionRenewalPaymentCardsParams = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode
    const subscriptionId = state.auth.account.luxPlus.member.subscription.item?.id

    if (!subscriptionId) return

    const currentRenewalPaymentCards = state.auth.account.luxPlus.member.subscription.renewalPaymentCards

    if (!params.refetch && (currentRenewalPaymentCards?.fetching || currentRenewalPaymentCards?.data)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_RENEWAL_PAYMENT_CARDS,
      request: () => getOffSessionPaymentMethodsByRegionAndPurposeId(region, subscriptionId),
    })
  }
}

export function setDisableMembership(disableMembership: boolean): AppAction {
  return {
    type: TOGGLE_SHOP_AS_MEMBER,
    disableMembership,
  }
}

export function toggleDisableMembership(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const disableMembership = state.auth.account.luxPlus.member.disableMembership
    dispatch(setDisableMembership(!disableMembership))
  }
}

export function fetchAccumulativePotentialSavings(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId
    const region = state.geo.currentRegionCode
    const isAlreadyMember = getCanRedeemLuxPlusBenefits(state)

    if (
      !customerId ||
      isAlreadyMember ||
      state.luxPlus.accumulativeSavings.fetching ||
      state.luxPlus.accumulativeSavings.data
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_ACCUMULATIVE_SAVINGS,
      request: () => getPotentialSavings({ customerId, region, daysPeriod: '365' }),
    })
  }
}

/**
 * Fetches LuxPlus order savings for a given order.
 * First tries to fetch redeemed benefits (for members), then falls back to potential savings (for non-members).
 */
export function fetchLuxPlusOrderSavings(orderId: string, region: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    // hide banner if luxplus not supported in current region
    if (!getIsLuxPlusEnabled(state) || !orderId) {
      return
    }

    // Check if already fetching or fetched
    const existingSavings = state.luxPlus.orderSavings[orderId]
    if (existingSavings?.fetching || existingSavings?.data) {
      return
    }

    // First, try redeemed benefits (indicates they were a member at booking)
    try {
      const redeemedData = await getRedeemedBenefitsByOrderId(orderId)

      // Only use redeemed savings if there are actual savings
      if (redeemedData.totalSavings > 0) {
        dispatch({
          type: API_CALL,
          api: FETCH_LUX_PLUS_ORDER_REDEEMED_SAVINGS,
          orderId,
          request: () => Promise.resolve(redeemedData),
        })
        return
      }
    } catch {
      // Expected to fail for non-members - fall through to potential savings
    }

    // Try potential savings for non-members
    const customerId = getState().auth.account.memberId
    if (!customerId) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_ORDER_POTENTIAL_SAVINGS,
      orderId,
      request: () => getPotentialSavings({ customerId, region, orderId }),
    })
  }
}

export function fetchTotalPotentialSavings(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId

    if (
      !customerId ||
      state.luxPlus.totalPotentialSavings.fetching ||
      state.luxPlus.totalPotentialSavings.data
    ) {
      return
    }

    const region = state.geo.currentRegionCode
    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_TOTAL_POTENTIAL_SAVINGS,
      request: () => getTotalPotentialSavings(customerId, region),
    })
  }
}

export function fetchMembershipMetrics(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.luxPlus.membershipMetrics.fetching ||
      state.luxPlus.membershipMetrics.data
    ) {
      return
    }

    const region = state.geo.currentRegionCode
    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_MEMBERSHIP_METRICS,
      request: () => getMembershipMetrics(region),
    })
  }
}
